/**
 * eBook reader constants.
 *
 * Endpoint paths verified against QA (backend-qa.studyiq.net) on 2026-08-03.
 * They differ from the "eBook Annotation API Requirements" document in several
 * places, so the live behaviour is what is encoded here:
 *
 *   PRD document                  actual
 *   ----------------------------  ------------------------------------------
 *   /ebook/annotations            app-content-ws/v1/ebook/annotations/save
 *   GET /ebook/annotations        POST .../annotations/fetch  (POST, not GET)
 *   PUT /ebook/annotations/{id}   PUT  .../annotations/update (id inside the
 *                                 annotations[] array, not in the path)
 *   DELETE /ebook/annotations/{id} DELETE .../annotations/delete?annotationId=
 *   /ebook/last-page              .../progress/save and .../progress/get
 *   pageNumber                    pageNo
 *   (absent)                      contentId is required on every call
 *
 * `annotations/get`, `annotations/list` and a bare `annotations` path do not
 * exist — only `save` / `fetch` / `update` / `delete`.
 */
const EBOOK_BASE = 'app-content-ws/v1/ebook';

export const EBOOK_ANNOTATIONS_SAVE_URL = `${EBOOK_BASE}/annotations/save`;
export const EBOOK_ANNOTATIONS_FETCH_URL = `${EBOOK_BASE}/annotations/fetch`;
export const EBOOK_ANNOTATIONS_UPDATE_URL = `${EBOOK_BASE}/annotations/update`;
export const EBOOK_ANNOTATIONS_DELETE_URL = `${EBOOK_BASE}/annotations/delete`;
export const EBOOK_PROGRESS_SAVE_URL = `${EBOOK_BASE}/progress/save`;
export const EBOOK_PROGRESS_GET_URL = `${EBOOK_BASE}/progress/get`;

export const ANNOTATION_TYPE = {
  NOTE: 'note',
  HIGHLIGHT: 'highlight',
};

// Swatches shown in the selection popover, in design order.
export const HIGHLIGHT_COLORS = [
  { name: 'teal', hex: '#4FD1C5' },
  { name: 'green', hex: '#3CC13B' },
  { name: 'blue', hex: '#98A2F3' },
  { name: 'purple', hex: '#C13BC1' },
];

// Applied to a live selection before the user picks a swatch.
export const PENDING_HIGHLIGHT_COLOR = 'yellow';

// Superset of HIGHLIGHT_COLORS: the API contract's examples use "yellow" and
// "blue", and the mobile apps may write colours the web picker doesn't offer,
// so anything we can be handed still renders.
export const HIGHLIGHT_COLOR_HEX = {
  yellow: '#FCE96A',
  teal: '#4FD1C5',
  green: '#3CC13B',
  blue: '#98A2F3',
  purple: '#C13BC1',
  pink: '#F58FC2',
  orange: '#FBA94C',
};

export const DEFAULT_HIGHLIGHT_HEX = HIGHLIGHT_COLOR_HEX.yellow;

// Opacity for the highlight overlay. Low enough that the rendered glyphs
// underneath stay legible.
export const HIGHLIGHT_OPACITY = 0.4;

export const ZOOM = {
  MIN: 0.5,
  MAX: 3,
  STEP: 0.25,
  DEFAULT: 1.2,
};

// Reading position is persisted at most this often while scrolling.
export const LAST_PAGE_SAVE_DEBOUNCE_MS = 1500;

export const VIEW_MODE = {
  CONTINUOUS: 'continuous',
  SINGLE: 'single',
};

// Reader preferences are per-device, not per-account: they describe how you
// like to read on this screen, so they stay in localStorage rather than syncing.
export const STORAGE_KEY = {
  DARK_MODE: 'ebook.darkMode',
  VIEW_MODE: 'ebook.viewMode',
};

/**
 * Shown before a secure book opens. This is a LEGAL control, not a technical
 * one: it does not stop anything, it establishes that the reader was told the
 * material is copyrighted before they saw it, which is what a takedown or an
 * infringement claim needs. The technical controls (watermark, concealment,
 * server-side URL) are separate and documented where they live.
 */
export const EBOOK_COPYRIGHT_NOTICE =
  'I understand that the E-book is Proprietary & Copyrighted Material of StudyIQ. ' +
  'Any reproduction in any form, physical or electronic mode on public forum etc will lead to ' +
  'infringement of Copyright of StudyIQ and will attract penal actions including FIR and claim ' +
  'of damages under applicable Copyright law.';
